import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Button,
  Container,
  Row,
  Col,
  Table,
  Modal,
  Form,
  Dropdown,
  DropdownButton,
  Pagination,
  Badge,
  InputGroup,
  FormControl
} from "react-bootstrap";
import { db } from "../../firebase";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  setDoc,
} from "firebase/firestore";
import AdminHeader from "../../Components/AdminHeader";
import Sidebar from "../../Components/Sidebar";
import { StepContext } from "../../Context/StepContext";
import { FaSearch, FaEdit, FaTrash, FaEye, FaClipboardCheck } from "react-icons/fa";

export default function OrderDetails3() {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const { addPendingOrder } = useContext(StepContext);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 10;
  const pageLimit = 5;

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "Orders"));
        const fetchedOrders = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const parseDate = (dateStr) => {
          if (!dateStr) return new Date(0);
          if (dateStr.includes("-")) {
            return new Date(dateStr);
          }
          const [day, month, year] = dateStr.split("/");
          return new Date(year, month - 1, day);
        };

        fetchedOrders.sort((a, b) => parseDate(b.date) - parseDate(a.date));
        setOrders(fetchedOrders);
        setFilteredOrders(fetchedOrders);
      } catch (error) {
        console.error("Error fetching orders: ", error);
      }
    };

    fetchOrders();
  }, []);

  // Filter orders based on search term and status filter
  useEffect(() => {
    let result = orders;
    
    // Filter by search term
    if (searchTerm) {
      result = result.filter(order => 
        (order.details?.name && order.details.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (order.details?.phoneNumber && order.details.phoneNumber.includes(searchTerm)) ||
        (order.details?.vinNumber && order.details.vinNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (order.id && order.id.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Filter by status
    if (statusFilter !== "All") {
      result = result.filter(order => order.status === statusFilter);
    }
    
    setFilteredOrders(result);
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchTerm, statusFilter, orders]);

  // Get current orders for pagination
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);

  // Function to render pagination items
  const renderPaginationItems = () => {
    let items = [];
    let startPage = Math.max(1, currentPage - Math.floor(pageLimit / 2));
    let endPage = startPage + pageLimit - 1;
    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - pageLimit + 1);
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => setCurrentPage(number)}
        >
          {number}
        </Pagination.Item>
      );
    }
    return items;
  };

  // Handler functions
  const handleShowDetails = (order) => {
    setCurrentOrder(order);
    setShowDetailsModal(true);
  };

  const handleShowEdit = (order) => {
    setCurrentOrder(order);
    setShowEditModal(true);
  };

  const handleShowStatus = (order) => {
    setCurrentOrder(order);
    setShowStatusModal(true);
  };

  const handleShowDeleteConfirm = (order) => {
    setCurrentOrder(order);
    setShowDeleteConfirmModal(true);
  };

  const handleDeleteOrder = async () => {
    try {
      await deleteDoc(doc(db, "Orders", currentOrder.id));
      setOrders(orders.filter((order) => order.id !== currentOrder.id));
      setFilteredOrders(filteredOrders.filter((order) => order.id !== currentOrder.id));
      setShowDeleteConfirmModal(false);
    } catch (error) {
      console.error("Error deleting order: ", error);
    }
  };

  const handleEditChange = (field, value) => {
    setCurrentOrder((prevOrder) => ({
      ...prevOrder,
      details: {
        ...prevOrder.details,
        [field]: value,
      },
    }));
  };

  const handleSaveChanges = async () => {
    try {
      const orderRef = doc(db, "Orders", currentOrder.id);
      await updateDoc(orderRef, currentOrder);
      
      // Update both orders arrays
      const updateOrdersList = (list) => list.map(o => o.id === currentOrder.id ? currentOrder : o);
      setOrders(updateOrdersList(orders));
      setFilteredOrders(updateOrdersList(filteredOrders));
      
      setShowEditModal(false);
    } catch (error) {
      console.error("Error updating order: ", error);
    }
  };

  const handleStatusChange = async (status) => {
    const updatedOrder = { ...currentOrder, status };

    try {
      // Update the order in the "Orders" collection
      const orderRef = doc(db, "Orders", updatedOrder.id);
      await updateDoc(orderRef, updatedOrder);

      // Update local state
      const updateOrdersList = (list) => list.map(o => o.id === updatedOrder.id ? updatedOrder : o);
      setOrders(updateOrdersList(orders));
      setFilteredOrders(updateOrdersList(filteredOrders));
      
      setShowStatusModal(false);

      // Reference to the document in "PendingOrders"
      const pendingOrderRef = doc(db, "PendingOrders", updatedOrder.id);

      if (status === "Pending") {
        // Create or update the PendingOrders document
        await setDoc(pendingOrderRef, updatedOrder, { merge: true });
      } else {
        // Remove the order from PendingOrders if it exists
        await deleteDoc(pendingOrderRef);
      }
    } catch (error) {
      console.error("Error updating status: ", error);
    }
  };

  // Get status badge variant
  const getStatusBadgeVariant = (status) => {
    switch(status) {
      case "Delivered": return "success";
      case "Cancelled": return "danger";
      case "Pending": return "warning";
      default: return "secondary";
    }
  };

  return (
    <div className="admin-dashboard d-flex flex-column min-vh-100">
      <AdminHeader />
      
      <div className="d-flex flex-grow-1">
        <Sidebar />
        
        <main className="flex-grow-1 bg-light">
          <Container fluid className="py-4">
            <Card className="shadow-sm border-0">
              <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                <h5 className="mb-0 font-weight-bold text-primary">Order Management</h5>
                <div className="d-flex">
                  <DropdownButton 
                    id="status-filter" 
                    title={`Status: ${statusFilter}`} 
                    variant="outline-secondary"
                    className="me-2"
                  >
                    <Dropdown.Item onClick={() => setStatusFilter("All")}>All</Dropdown.Item>
                    <Dropdown.Item onClick={() => setStatusFilter("Pending")}>Pending</Dropdown.Item>
                    <Dropdown.Item onClick={() => setStatusFilter("Delivered")}>Delivered</Dropdown.Item>
                    <Dropdown.Item onClick={() => setStatusFilter("Cancelled")}>Cancelled</Dropdown.Item>
                  </DropdownButton>
                  
                  <InputGroup>
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                    <FormControl
                      placeholder="Search orders..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </div>
              </Card.Header>
              
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table className="table-hover mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="py-3">Order ID</th>
                        <th className="py-3">Date</th>
                        <th className="py-3">Customer</th>
                        <th className="py-3">Car Details</th>
                        <th className="py-3">Total</th>
                        <th className="py-3">Status</th>
                        <th className="py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentOrders.length > 0 ? (
                        currentOrders.map((order) => (
                          <tr key={order.id}>
                            <td className="py-3">
                              <small className="text-muted fw-bold">{order.id.substring(0, 8)}...</small>
                            </td>
                            <td className="py-3">{order.date || "N/A"}</td>
                            <td className="py-3">
                              <div className="d-flex flex-column">
                                <span className="fw-bold">{order.details?.name || "N/A"}</span>
                                <small className="text-muted">{order.details?.phoneNumber || "N/A"}</small>
                              </div>
                            </td>
                            <td className="py-3">
                              <div className="d-flex flex-column">
                                <span>{order.details?.carModel || "N/A"} ({order.details?.carYear || "N/A"})</span>
                                <small className="text-muted">VIN: {order.details?.vinNumber || "N/A"}</small>
                              </div>
                            </td>
                            <td className="py-3 fw-bold">${order.totalPrice || "0"}</td>
                            <td className="py-3">
                              <Badge bg={getStatusBadgeVariant(order.status || "Pending")}>
                                {order.status || "Pending"}
                              </Badge>
                            </td>
                            <td className="py-3">
                              <div className="d-flex">
                                <Button 
                                  variant="outline-primary" 
                                  size="sm" 
                                  className="me-1"
                                  onClick={() => handleShowDetails(order)}
                                  title="View Details"
                                >
                                  <FaEye />
                                </Button>
                                <Button 
                                  variant="outline-success" 
                                  size="sm" 
                                  className="me-1"
                                  onClick={() => handleShowEdit(order)}
                                  title="Edit Order"
                                >
                                  <FaEdit />
                                </Button>
                                <Button 
                                  variant="outline-warning" 
                                  size="sm" 
                                  className="me-1"
                                  onClick={() => handleShowStatus(order)}
                                  title="Update Status"
                                >
                                  <FaClipboardCheck />
                                </Button>
                                <Button 
                                  variant="outline-danger" 
                                  size="sm"
                                  onClick={() => handleShowDeleteConfirm(order)}
                                  title="Delete Order"
                                >
                                  <FaTrash />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            {searchTerm || statusFilter !== "All" 
                              ? "No orders match your search criteria." 
                              : "No orders found."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
              
              <Card.Footer className="bg-white border-0 px-4 py-3">
                <Row className="align-items-center">
                  <Col>
                    <small className="text-muted">
                      Showing {filteredOrders.length > 0 ? indexOfFirstOrder + 1 : 0} to {Math.min(indexOfLastOrder, filteredOrders.length)} of {filteredOrders.length} orders
                    </small>
                  </Col>
                  <Col className="d-flex justify-content-end">
                    {totalPages > 1 && (
                      <Pagination className="mb-0">
                        <Pagination.First
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                        />
                        <Pagination.Prev
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1}
                        />
                        {renderPaginationItems()}
                        <Pagination.Next
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        />
                        <Pagination.Last
                          onClick={() => setCurrentPage(totalPages)}
                          disabled={currentPage === totalPages}
                        />
                      </Pagination>
                    )}
                  </Col>
                </Row>
              </Card.Footer>
            </Card>
          </Container>
        </main>
      </div>

      {/* Order Details Modal */}
      {currentOrder && (
        <Modal 
          show={showDetailsModal} 
          onHide={() => setShowDetailsModal(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton className="bg-light">
            <Modal.Title>
              <div className="d-flex flex-column">
                <span>Order #{currentOrder.id.substring(0, 8)}...</span>
                <Badge bg={getStatusBadgeVariant(currentOrder.status || "Pending")} className="mt-1">
                  {currentOrder.status || "Pending"}
                </Badge>
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Card className="border-0 mb-4 shadow-sm">
                  <Card.Header className="bg-light">
                    <h6 className="mb-0">Customer Information</h6>
                  </Card.Header>
                  <Card.Body>
                    <p className="mb-1"><strong>Name:</strong> {currentOrder.details?.name || "N/A"}</p>
                    <p className="mb-1"><strong>Email:</strong> {currentOrder.details?.email || "N/A"}</p>
                    <p className="mb-1"><strong>Phone:</strong> {currentOrder.details?.phoneNumber || "N/A"}</p>
                    <p className="mb-1"><strong>Address:</strong> {currentOrder.details?.address || "N/A"}</p>
                    <p className="mb-1"><strong>State/Country:</strong> {currentOrder.details?.state || "N/A"}, {currentOrder.details?.country || "N/A"}</p>
                    <p className="mb-0"><strong>Postcode:</strong> {currentOrder.details?.zip || "N/A"}</p>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={6}>
                <Card className="border-0 mb-4 shadow-sm">
                  <Card.Header className="bg-light">
                    <h6 className="mb-0">Order Summary</h6>
                  </Card.Header>
                  <Card.Body>
                    <p className="mb-1"><strong>Order Date:</strong> {currentOrder.date || "N/A"}</p>
                    <p className="mb-1"><strong>Total Price:</strong> <span className="text-success fw-bold">${currentOrder.totalPrice || "0"}</span></p>
                    <p className="mb-0"><strong>Discount:</strong> ${currentOrder.discount || "0"}</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            
            <Card className="border-0 mb-4 shadow-sm">
              <Card.Header className="bg-light">
                <h6 className="mb-0">Car Information</h6>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <p className="mb-1"><strong>Car Model:</strong> {currentOrder.details?.carModel || "N/A"}</p>
                    <p className="mb-1"><strong>Car Year:</strong> {currentOrder.details?.carYear || "N/A"}</p>
                    <p className="mb-1"><strong>VIN Number:</strong> {currentOrder.details?.vinNumber || "N/A"}</p>
                  </Col>
                  <Col md={6}>
                    <p className="mb-1"><strong>Color:</strong> {currentOrder.details?.color || "N/A"}</p>
                    <p className="mb-1"><strong>Mileage (KM):</strong> {currentOrder.details?.currentKm || "N/A"}</p>
                    <p className="mb-1"><strong>Accident Report:</strong> {currentOrder.details?.accidentOrWrittenOff || "N/A"}</p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
            
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-light">
                <h6 className="mb-0">Ordered Products</h6>
              </Card.Header>
              <Card.Body className="p-0">
                <Table responsive className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Name</th>
                      <th>Description</th>
                      <th className="text-end">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(currentOrder.cartItems || []).length > 0 ? (
                      currentOrder.cartItems.map((item, index) => (
                        <tr key={index}>
                          <td>{item.name || "N/A"}</td>
                          <td>{item.description || "N/A"}</td>
                          <td className="text-end fw-bold">${item.totalPrice || "0"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center">No products found</td>
                      </tr>
                    )}
                  </tbody>
                  {currentOrder.cartItems && currentOrder.cartItems.length > 0 && (
                    <tfoot className="table-light">
                      <tr>
                        <td colSpan="2" className="text-end fw-bold">Total:</td>
                        <td className="text-end fw-bold text-success">${currentOrder.totalPrice || "0"}</td>
                      </tr>
                    </tfoot>
                  )}
                </Table>
              </Card.Body>
            </Card>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
              Close
            </Button>
            <Button variant="success" onClick={() => {
              setShowDetailsModal(false);
              handleShowEdit(currentOrder);
            }}>
              Edit Order
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Edit Order Modal */}
      {currentOrder && (
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Order</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <h6 className="text-primary mb-3">Customer Information</h6>
              <Row>
                <Col md={6}>
                  <Form.Group controlId="formCustomerName" className="mb-3">
                    <Form.Label>Customer Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={currentOrder.details?.name || ""}
                      onChange={(e) => handleEditChange("name", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="formPhone" className="mb-3">
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      type="text"
                      value={currentOrder.details?.phoneNumber || ""}
                      onChange={(e) => handleEditChange("phoneNumber", e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group controlId="formEmail" className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={currentOrder.details?.email || ""}
                      onChange={(e) => handleEditChange("email", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="formZip" className="mb-3">
                    <Form.Label>Postcode</Form.Label>
                    <Form.Control
                      type="text"
                      value={currentOrder.details?.zip || ""}
                      onChange={(e) => handleEditChange("zip", e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <h6 className="text-primary mb-3 mt-4">Car Information</h6>
              <Row>
                <Col md={6}>
                  <Form.Group controlId="formCarModel" className="mb-3">
                    <Form.Label>Car Model</Form.Label>
                    <Form.Control
                      type="text"
                      value={currentOrder.details?.carModel || ""}
                      onChange={(e) => handleEditChange("carModel", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="formCarYear" className="mb-3">
                    <Form.Label>Car Year</Form.Label>
                    <Form.Control
                      type="text"
                      value={currentOrder.details?.carYear || ""}
                      onChange={(e) => handleEditChange("carYear", e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group controlId="formVin" className="mb-3">
                    <Form.Label>VIN Number</Form.Label>
                    <Form.Control
                      type="text"
                      value={currentOrder.details?.vinNumber || ""}
                      onChange={(e) => handleEditChange("vinNumber", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="formColor" className="mb-3">
                    <Form.Label>Color</Form.Label>
                    <Form.Control
                      type="text"
                      value={currentOrder.details?.color || ""}
                      onChange={(e) => handleEditChange("color", e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group controlId="formMileage" className="mb-3">
                    <Form.Label>Mileage (KM)</Form.Label>
                    <Form.Control
                      type="text"
                      value={currentOrder.details?.currentKm || ""}
                      onChange={(e) => handleEditChange("currentKm", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="formAccident" className="mb-3">
                    <Form.Label>Accident Report</Form.Label>
                    <Form.Control
                      type="text"
                      value={currentOrder.details?.accidentOrWrittenOff || ""}
                      onChange={(e) => handleEditChange("accidentOrWrittenOff", e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSaveChanges}>
              Save Changes
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Status Modal */}
      {currentOrder && (
        <Modal 
          show={showStatusModal} 
          onHide={() => setShowStatusModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Update Order Status</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center py-4">
            <h6 className="mb-4">Current Status: 
              <Badge bg={getStatusBadgeVariant(currentOrder.status || "Pending")} className="ms-2">
                {currentOrder.status || "Pending"}
              </Badge>
            </h6>
            
            <div className="d-flex justify-content-center gap-2">
              <Button 
                variant={currentOrder.status === "Pending" ? "warning" : "outline-warning"}
                onClick={() => handleStatusChange("Pending")}
                className="px-4"
              >
                Pending
              </Button>
              <Button 
                variant={currentOrder.status === "Delivered" ? "success" : "outline-success"}
                onClick={() => handleStatusChange("Delivered")}
                className="px-4"
              >
                Delivered
              </Button>
              <Button 
                variant={currentOrder.status === "Cancelled" ? "danger" : "outline-danger"}
                onClick={() => handleStatusChange("Cancelled")}
                className="px-4"
              >
                Cancelled
              </Button>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowStatusModal(false)}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {currentOrder && (
        <Modal 
          show={showDeleteConfirmModal} 
          onHide={() => setShowDeleteConfirmModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Confirm Deletion</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center py-4">
            <div className="mb-3 text-danger">
              <FaTrash size={48} />
            </div>
            <h5>Are you sure you want to delete this order?</h5>
            <p className="text-muted mb-0">
              Order #{currentOrder.id.substring(0, 8)}... for {currentOrder.details?.name || "Unknown Customer"}
            </p>
            <p className="text-danger mt-3">
              This action cannot be undone.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowDeleteConfirmModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteOrder}
            >
              Delete Order
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}